// src/components/festival/OgieneniPortal.tsx
// DROP 36 — Royal Palace Portal | DROP 40F AUDIT CLEAN
import React, { useState, useEffect, useCallback } from 'react';
import {
  Crown, MapPin, Shield, Star,
  Edit2, Plus, Trash2,
  CheckCircle, AlertCircle,
  ChevronDown, ChevronUp,
  Phone, Mail, Globe,
} from 'lucide-react';

const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
  green:'#0F5132', gold:'#C9A84C', goldLt:'#E2C97E', red:'#ef4444',
};

const inp: React.CSSProperties = {
  width:'100%', background:C.bg, border:`1px solid ${C.border}`,
  borderRadius:7, padding:'9px 12px', color:C.text, fontSize:13,
  outline:'none', boxSizing:'border-box',
};

interface RoyalMessage {
  id: string;
  title: string;
  date: string;
  occasion: string;
  excerpt: string;
  fullText: string;
  isPublic: boolean;
}

interface PalaceOfficial {
  id: string;
  name: string;
  title: string;
  role: string;
  department: string;
  phone?: string;
  email?: string;
  photo?: string;
  displayOrder: number;
}

interface PalaceInfo {
  patronMessage: string;
  aboutText: string;
  phone: string;
  email: string;
  address: string;
  officeHours: string;
}

interface Props { currentUser?: any; }

const TOKEN = () =>
  localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';

const DEFAULT_MESSAGES: RoyalMessage[] = [
  {
    id:'msg-1',
    title:'Royal Welcome Address — ERUE Festival 2026',
    date:'December 24, 2026',
    occasion:'Grand Opening Ceremony',
    isPublic:true,
    excerpt:'In the name of our ancestors, in the spirit of Uzairue unity...',
    fullText:'In the name of our ancestors, in the spirit of Uzairue unity, and under the divine blessing of Almighty God, I welcome all sons and daughters of Uzairue Kingdom — those present here at the Palace, those joining us from across Nigeria, and our beloved Diaspora watching from every corner of the globe. The Annual ERUE Festival is not merely a celebration. It is a covenant — a living testimony that we are One People, One Kingdom, One Destiny.',
  },
  {
    id:'msg-2',
    title:'Royal Message on Community Development',
    date:'December 28, 2026',
    occasion:'Diaspora Day Address',
    isPublic:true,
    excerpt:'To our Diaspora: your return is not just physical — it is spiritual...',
    fullText:'To our Diaspora: your return is not just physical — it is spiritual. Every investment you make in Uzairue is an investment in the legacy of your forebears and the future of your children. I call upon every Diaspora son and daughter to look at our 22 villages and component communities — each with its unique gifts, heritage, and needs — and ask: what can I give? The Palace stands ready to facilitate, to guide, and to honour every act of community investment. Together, we shall transform Uzairue Kingdom into the beacon of development that Etsako West deserves. One People. One Kingdom. One Destiny.',
  },
  {
    id:'msg-3',
    title:'Royal Closing Benediction — ERUE Festival 2026',
    date:'December 30, 2026',
    occasion:'Grand Closing Ceremony',
    isPublic:true,
    excerpt:'As we conclude this glorious festival, may the blessings of our ancestors follow each of you home...',
    fullText:'As we conclude this glorious Annual ERUE Festival 2026, my heart overflows with gratitude. To the Local Organising Committee, to our Central Working Committee, and to each of our 22 villages and component communities — you have made this festival a testimony to the greatness of Uzairue. May the blessings of our ancestors follow each of you home. Until we gather again, carry Uzairue in your hearts. One People, One Kingdom, One Destiny.',
  },
];

const DEFAULT_INFO: PalaceInfo = {
  patronMessage: 'The Palace of the Ogieneni of Uzairue Kingdom extends a royal welcome to all sons and daughters of Uzairue — at home and in the Diaspora — to the Annual ERUE Festival 2026.',
  aboutText: 'The Palace of the Ogieneni of Uzairue Kingdom stands at the heart of Jattu-Uzairue, serving as the seat of royal authority, cultural heritage, and community governance for all 22 villages and component communities of Uzairue Kingdom, Etsako West LGA, Edo State, Nigeria.',
  phone: '+234 705 7840 320',
  email: 'erueuzairue@gmail.com',
  address: 'Palace of the Ogieneni of Uzairue Kingdom, Jattu-Uzairue, Etsako West LGA, Edo State, Nigeria',
  officeHours: 'Monday – Friday: 9:00 AM – 4:00 PM WAT',
};

const PALACE_FACTS = [
  { icon:'📍', label:'Location',           value:'Jattu-Uzairue, Etsako West LGA, Edo State, Nigeria' },
  { icon:'👑', label:'Title',              value:'The Ogieneni of Uzairue Kingdom' },
  { icon:'🌍', label:'Kingdom',            value:'Uzairue Kingdom — 22 villages and component communities' },
  { icon:'🏘️', label:'Communities',       value:'22 villages and 4 special communities' },
  { icon:'👥', label:'Population (est.)',  value:'168,500 across all communities' },
  { icon:'🏛️', label:'LGA',              value:'Etsako West Local Government Area' },
  { icon:'🗺️', label:'State',            value:'Edo State, Federal Republic of Nigeria' },
  { icon:'🎯', label:'Motto',             value:'"One People, One Kingdom, One Destiny"' },
];

/* ═══════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════ */
const OgieneniPortal: React.FC<Props> = ({ currentUser }) => {
  const [messages,    setMessages]    = useState<RoyalMessage[]>(DEFAULT_MESSAGES);
  const [officials,   setOfficials]   = useState<PalaceOfficial[]>([]);
  const [palaceInfo,  setPalaceInfo]  = useState<PalaceInfo>(DEFAULT_INFO);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState<'palace'|'messages'|'structure'|'admin'>('palace');
  const [expandedMsg, setExpandedMsg] = useState<string|null>(null);
  const [msg,         setMsg]         = useState<{type:'ok'|'err';text:string}|null>(null);
  const [showMsgForm,      setShowMsgForm]      = useState(false);
  const [showOfficialForm, setShowOfficialForm] = useState(false);
  const [editMsg,     setEditMsg]     = useState<RoyalMessage|null>(null);
  const [msgForm,     setMsgForm]     = useState({
    title:'', date:'', occasion:'', excerpt:'', fullText:'', isPublic:true,
  });
  const [officialForm, setOfficialForm] = useState({
    name:'', title:'', role:'', department:'Palace Administration',
    phone:'', email:'', photo:'', displayOrder:0,
  });
  const [editInfo,  setEditInfo]  = useState(false);
  const [infoForm,  setInfoForm]  = useState({ ...DEFAULT_INFO });
  const [saving,    setSaving]    = useState(false);

  const isAdmin = !!(currentUser && ['PLATFORM_ADMIN','SECRETARY','FESTIVAL_COORDINATOR']
    .includes((currentUser.systemRole || currentUser.role || '').toUpperCase()));

  const flash = (type: 'ok'|'err', text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/public/palace');
      const d = await r.json();
      if (d.success) {
        if (d.messages?.length)  setMessages(d.messages);
        if (d.officials?.length) setOfficials(d.officials);
        if (d.info)              setPalaceInfo(d.info);
      }
    } catch { /* use defaults silently */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePalaceData = async (data: {
    messages?: RoyalMessage[];
    officials?: PalaceOfficial[];
    info?: PalaceInfo;
  }) => {
    setSaving(true);
    try {
      const r = await fetch('/api/admin/palace', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${TOKEN()}` },
        body: JSON.stringify({
          messages: data.messages || messages,
          officials: data.officials || officials,
          info: data.info || palaceInfo,
        }),
      });
      const d = await r.json();
      if (d.success) {
        if (data.messages)  setMessages(data.messages);
        if (data.officials) setOfficials(data.officials);
        if (data.info)      setPalaceInfo(data.info);
        flash('ok', 'Palace data saved.');
      } else flash('err', d.error || 'Save failed');
    } catch { flash('err', 'Network error'); }
    setSaving(false);
  };

  const handleSaveMsg = async () => {
    if (!msgForm.title.trim()) { flash('err', 'Title required'); return; }
    let updated: RoyalMessage[];
    if (editMsg) {
      updated = messages.map(m => m.id === editMsg.id ? { ...editMsg, ...msgForm } : m);
    } else {
      updated = [...messages, { id:`msg-${Date.now()}`, ...msgForm }];
    }
    await savePalaceData({ messages: updated });
    setShowMsgForm(false);
    setEditMsg(null);
    setMsgForm({ title:'', date:'', occasion:'', excerpt:'', fullText:'', isPublic:true });
  };

  const handleDeleteMsg = async (id: string) => {
    await savePalaceData({ messages: messages.filter(m => m.id !== id) });
  };

  const handleSaveOfficial = async () => {
    if (!officialForm.name.trim()) { flash('err', 'Name required'); return; }
    const updated = [...officials, { id:`off-${Date.now()}`, ...officialForm }]
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    await savePalaceData({ officials: updated });
    setShowOfficialForm(false);
    setOfficialForm({ name:'', title:'', role:'', department:'Palace Administration',
      phone:'', email:'', photo:'', displayOrder:0 });
  };

  const handleDeleteOfficial = async (id: string) => {
    await savePalaceData({ officials: officials.filter(o => o.id !== id) });
  };

  const handleSaveInfo = async () => {
    await savePalaceData({ info: infoForm });
    setEditInfo(false);
  };

  /* ── Loading State ──────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ minHeight:'60vh', display:'flex', alignItems:'center',
        justifyContent:'center', background:C.bg }}>
        <div style={{ textAlign:'center' }}>
          <Crown size={40} style={{ color:C.gold, margin:'0 auto 16px', display:'block',
            animation:'pulse 1.5s ease-in-out infinite' }} />
          <p style={{ color:C.muted, fontSize:14 }}>Loading Royal Palace Portal…</p>
        </div>
      </div>
    );
  }

  /* ── Main Render ─────────────────────────────────────────────────────────── */
  return (
    <div style={{ background:C.bg, minHeight:'100vh', padding:'24px 0' }}>
      <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 20px' }}>

        {/* ── Royal Header ────────────────────────────────────────────────── */}
        <div style={{
          background:'linear-gradient(135deg,#060F09 0%,#0A1A10 50%,#0F5132 100%)',
          borderRadius:16, padding:'36px 32px', marginBottom:24,
          border:`1px solid rgba(201,168,76,0.3)`, position:'relative', overflow:'hidden',
        }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200,
            borderRadius:'50%', background:'rgba(201,168,76,0.04)' }}/>
          <div style={{ position:'absolute', bottom:-30, left:-30, width:120, height:120,
            borderRadius:'50%', background:'rgba(201,168,76,0.04)' }}/>

          <div style={{ display:'flex', alignItems:'flex-start', gap:20,
            flexWrap:'wrap', position:'relative' }}>

            {/* Ogieneni Photo */}
            <div style={{ width:90, height:90, borderRadius:'50%', flexShrink:0,
              border:`2px solid rgba(201,168,76,0.5)`, overflow:'hidden',
              background:'rgba(201,168,76,0.1)', display:'flex',
              alignItems:'center', justifyContent:'center' }}>
              <img
                src="https://res.cloudinary.com/uuovmrek/image/upload/ogienenimai002"
                alt="HRH The Ogieneni of Uzairue Kingdom"
                style={{ width:'100%', height:'100%', objectFit:'cover' }}
                onError={e => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent) {
                    const icon = document.createElement('div');
                    icon.innerHTML = '👑';
                    icon.style.fontSize = '36px';
                    parent.appendChild(icon);
                  }
                }}
              />
            </div>

            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:3,
                textTransform:'uppercase', color:C.gold, marginBottom:6 }}>
                Royal Palace · Uzairue Kingdom
              </div>
              <h1 style={{ fontFamily:'Cinzel,serif', fontSize:28, fontWeight:700,
                color:C.text, margin:'0 0 6px', lineHeight:1.2 }}>
                Palace of the Ogieneni
              </h1>
              <p style={{ fontSize:14, color:C.gold, fontStyle:'italic', margin:'0 0 8px' }}>
                HRH The Ogieneni of Uzairue Kingdom
              </p>
              <p style={{ fontSize:13, color:C.muted, margin:0, maxWidth:600, lineHeight:1.6 }}>
                {palaceInfo.patronMessage}
              </p>
              <div style={{ marginTop:14, display:'inline-flex', alignItems:'center', gap:6,
                padding:'6px 14px', background:'rgba(201,168,76,0.1)',
                border:`1px solid rgba(201,168,76,0.3)`, borderRadius:20 }}>
                <Star size={11} style={{ color:C.gold }} fill={C.gold} />
                <span style={{ fontSize:11, fontWeight:700, color:C.gold,
                  letterSpacing:2, textTransform:'uppercase' }}>
                  One People, One Kingdom, One Destiny
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Flash Message ────────────────────────────────────────────────── */}
        {msg && (
          <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:16,
            background:msg.type==='ok'?'rgba(34,197,94,0.1)':'rgba(239,68,68,0.1)',
            border:`1px solid ${msg.type==='ok'?'rgba(34,197,94,0.3)':'rgba(239,68,68,0.3)'}`,
            color:msg.type==='ok'?'#22c55e':C.red, fontSize:13,
            display:'flex', alignItems:'center', gap:8 }}>
            {msg.type==='ok' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
            {msg.text}
          </div>
        )}

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <div style={{ display:'flex', gap:4, marginBottom:24,
          borderBottom:`1px solid ${C.border}`, flexWrap:'wrap' }}>
          {([
            { id:'palace',    label:'🏛️ Royal Palace' },
            { id:'messages',  label:'📜 Royal Messages' },
            { id:'structure', label:'⚙️ Administration' },
            ...(isAdmin ? [{ id:'admin', label:'🔧 Edit Portal' }] : []),
          ] as { id:string; label:string }[]).map(t => (
            <button key={t.id} onClick={() => setTab(t.id as any)}
              style={{ padding:'10px 18px', background:'none', border:'none',
                borderBottom:`2px solid ${tab===t.id ? C.gold : 'transparent'}`,
                color:tab===t.id ? C.gold : C.muted,
                fontSize:13, fontWeight:600, cursor:'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            PALACE TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'palace' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

            {/* About */}
            <div style={{ gridColumn:'1 / -1', background:C.card,
              border:`1px solid ${C.border}`, borderRadius:12, padding:24 }}>
              <h2 style={{ fontFamily:'Cinzel,serif', fontSize:18, color:C.gold,
                margin:'0 0 14px', display:'flex', alignItems:'center', gap:8 }}>
                <Crown size={18}/> About the Royal Palace
              </h2>
              <p style={{ fontSize:14, color:C.muted, lineHeight:1.8, margin:0 }}>
                {palaceInfo.aboutText}
              </p>
            </div>

            {/* Kingdom Facts */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:12, padding:20 }}>
              <h3 style={{ fontFamily:'Cinzel,serif', fontSize:15, color:C.gold,
                margin:'0 0 14px' }}>Kingdom Facts</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {PALACE_FACTS.map((f, i) => (
                  <div key={i} style={{ display:'flex', gap:12, padding:'8px 12px',
                    background:`${C.bg}80`, borderRadius:8, border:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:16, flexShrink:0 }}>{f.icon}</span>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:C.dim,
                        textTransform:'uppercase', letterSpacing:1 }}>{f.label}</div>
                      <div style={{ fontSize:13, color:C.text }}>{f.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact + Palace Photo */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:12, padding:20 }}>
              <h3 style={{ fontFamily:'Cinzel,serif', fontSize:15, color:C.gold,
                margin:'0 0 14px' }}>Palace Contact</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[
                  { icon:<Phone size={14}/>, label:'Phone', value:palaceInfo.phone,
                    href:`tel:${palaceInfo.phone}` },
                  { icon:<Mail size={14}/>,  label:'Email', value:palaceInfo.email,
                    href:`mailto:${palaceInfo.email}` },
                  { icon:<MapPin size={14}/>,label:'Address', value:palaceInfo.address,
                    href:undefined },
                  { icon:<Globe size={14}/>, label:'Office Hours', value:palaceInfo.officeHours,
                    href:undefined },
                ].map((c, i) => (
                  <div key={i} style={{ display:'flex', gap:12, padding:'10px 14px',
                    background:`${C.bg}80`, borderRadius:8, border:`1px solid ${C.border}` }}>
                    <div style={{ color:C.gold, flexShrink:0, marginTop:1 }}>{c.icon}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:C.dim,
                        textTransform:'uppercase', letterSpacing:1, marginBottom:2 }}>{c.label}</div>
                      {c.href ? (
                        <a href={c.href} style={{ fontSize:13, color:C.text, textDecoration:'none' }}>
                          {c.value}
                        </a>
                      ) : (
                        <div style={{ fontSize:13, color:C.text }}>{c.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Palace Photo */}
              <div style={{ marginTop:16, borderRadius:10, overflow:'hidden',
                border:`1px solid rgba(201,168,76,0.2)` }}>
                <img
                  src="https://res.cloudinary.com/uuovmrek/image/upload/uzairuepalace001"
                  alt="Palace of the Ogieneni of Uzairue Kingdom"
                  style={{ width:'100%', height:180, objectFit:'cover', display:'block' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            MESSAGES TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'messages' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:20 }}>
              <h2 style={{ fontFamily:'Cinzel,serif', fontSize:18, color:C.text, margin:0 }}>
                Royal Messages & Addresses
              </h2>
              {isAdmin && (
                <button onClick={() => {
                  setEditMsg(null);
                  setMsgForm({ title:'', date:'', occasion:'', excerpt:'', fullText:'', isPublic:true });
                  setShowMsgForm(true);
                }} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
                  background:C.green, border:`1px solid rgba(201,168,76,0.3)`,
                  borderRadius:8, color:C.gold, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  <Plus size={13}/> Add Message
                </button>
              )}
            </div>

            {messages.filter(m => isAdmin || m.isPublic).length === 0 ? (
              <div style={{ textAlign:'center', padding:60, color:C.muted, fontSize:14 }}>
                No royal messages published yet.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                {messages.filter(m => isAdmin || m.isPublic).map(m => (
                  <div key={m.id} style={{ background:C.card,
                    border:`1px solid rgba(201,168,76,0.2)`, borderRadius:14, overflow:'hidden' }}>
                    <div style={{ padding:'20px 24px',
                      background:'linear-gradient(135deg,rgba(15,81,50,0.3),transparent)' }}>
                      <div style={{ display:'flex', alignItems:'flex-start',
                        justifyContent:'space-between', gap:10 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:10, color:C.gold, fontWeight:700,
                            letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>
                            {m.occasion} · {m.date}
                            {isAdmin && !m.isPublic && (
                              <span style={{ marginLeft:8, padding:'1px 6px', borderRadius:10,
                                background:'rgba(239,68,68,0.2)', color:C.red, fontSize:9 }}>
                                PRIVATE
                              </span>
                            )}
                          </div>
                          <h3 style={{ fontFamily:'Cinzel,serif', fontSize:18, color:C.text,
                            margin:'0 0 10px', lineHeight:1.3 }}>{m.title}</h3>
                          <p style={{ fontSize:13, color:C.muted, lineHeight:1.7,
                            margin:0, fontStyle:'italic' }}>
                            "{m.excerpt}"
                          </p>
                        </div>
                        <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                          {isAdmin && (
                            <>
                              <button onClick={() => {
                                setEditMsg(m);
                                setMsgForm({ title:m.title, date:m.date, occasion:m.occasion,
                                  excerpt:m.excerpt, fullText:m.fullText, isPublic:m.isPublic });
                                setShowMsgForm(true);
                              }} style={{ padding:'5px 10px', background:C.green,
                                border:`1px solid rgba(201,168,76,0.3)`, borderRadius:6,
                                color:C.gold, fontSize:11, cursor:'pointer' }}>
                                <Edit2 size={11}/>
                              </button>
                              <button onClick={() => handleDeleteMsg(m.id)}
                                style={{ padding:'5px 10px', background:'none',
                                  border:`1px solid rgba(239,68,68,0.3)`, borderRadius:6,
                                  color:C.red, fontSize:11, cursor:'pointer' }}>
                                <Trash2 size={11}/>
                              </button>
                            </>
                          )}
                          <button onClick={() => setExpandedMsg(expandedMsg===m.id ? null : m.id)}
                            style={{ padding:'5px 10px', background:'none',
                              border:`1px solid ${C.border}`, borderRadius:6,
                              color:C.muted, fontSize:11, cursor:'pointer' }}>
                            {expandedMsg===m.id ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                          </button>
                        </div>
                      </div>
                    </div>

                    {expandedMsg === m.id && (
                      <div style={{ padding:'16px 24px 24px',
                        borderTop:`1px solid rgba(201,168,76,0.15)` }}>
                        <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
                          <div style={{ width:3, minHeight:60, borderRadius:2,
                            background:`linear-gradient(${C.gold},rgba(201,168,76,0.3))`,
                            flexShrink:0 }}/>
                          <p style={{ fontSize:14, color:C.text, lineHeight:1.9,
                            margin:0, fontStyle:'italic' }}>
                            {m.fullText}
                          </p>
                        </div>
                        <div style={{ marginTop:14, textAlign:'right' as const }}>
                          <span style={{ fontSize:12, color:C.gold, fontWeight:600,
                            fontFamily:'Cinzel,serif' }}>
                            — HRH The Ogieneni of Uzairue Kingdom
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            STRUCTURE TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'structure' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:20 }}>
              <h2 style={{ fontFamily:'Cinzel,serif', fontSize:18, color:C.text, margin:0 }}>
                Palace Administrative Structure
              </h2>
              {isAdmin && (
                <button onClick={() => setShowOfficialForm(true)}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
                    background:C.green, border:`1px solid rgba(201,168,76,0.3)`,
                    borderRadius:8, color:C.gold, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  <Plus size={13}/> Add Official
                </button>
              )}
            </div>

            {officials.length === 0 ? (
              <div style={{ textAlign:'center', padding:60 }}>
                <Shield size={40} style={{ margin:'0 auto 16px', opacity:0.3,
                  display:'block', color:C.muted }}/>
                <p style={{ fontSize:14, color:C.muted, marginBottom:16 }}>
                  Administrative structure not yet configured.
                </p>
                {isAdmin && (
                  <button onClick={() => setShowOfficialForm(true)}
                    style={{ padding:'10px 24px', background:C.green,
                      border:`1px solid rgba(201,168,76,0.4)`, borderRadius:10,
                      color:C.gold, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                    + Add First Official
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display:'grid',
                gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
                {[...new Set(officials.map(o => o.department))].map(dept => (
                  <div key={dept}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.gold,
                      letterSpacing:1, textTransform:'uppercase', marginBottom:10,
                      paddingBottom:6, borderBottom:`1px solid rgba(201,168,76,0.2)` }}>
                      {dept}
                    </div>
                    {officials
                      .filter(o => o.department === dept)
                      .sort((a, b) => (a.displayOrder||0) - (b.displayOrder||0))
                      .map(off => (
                        <div key={off.id} style={{ background:C.card,
                          border:`1px solid ${C.border}`, borderRadius:10,
                          padding:'14px 16px', marginBottom:10,
                          display:'flex', alignItems:'center', gap:12 }}>
                          {off.photo ? (
                            <img src={off.photo} alt={off.name}
                              style={{ width:40, height:40, borderRadius:'50%',
                                objectFit:'cover', flexShrink:0,
                                border:`1px solid rgba(201,168,76,0.3)` }}
                              onError={e => { (e.target as HTMLImageElement).style.display='none'; }}/>
                          ) : (
                            <div style={{ width:40, height:40, borderRadius:'50%',
                              background:'rgba(201,168,76,0.1)',
                              border:`1px solid rgba(201,168,76,0.3)`,
                              display:'flex', alignItems:'center', justifyContent:'center',
                              flexShrink:0 }}>
                              <Crown size={18} style={{ color:C.gold }}/>
                            </div>
                          )}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:C.text,
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {off.title ? `${off.title} ` : ''}{off.name}
                            </div>
                            <div style={{ fontSize:11, color:C.gold }}>{off.role}</div>
                            {off.phone && (
                              <a href={`tel:${off.phone}`}
                                style={{ fontSize:10, color:C.dim, textDecoration:'none' }}>
                                📞 {off.phone}
                              </a>
                            )}
                          </div>
                          {isAdmin && (
                            <button onClick={() => handleDeleteOfficial(off.id)}
                              style={{ padding:'4px 8px', background:'none',
                                border:`1px solid rgba(239,68,68,0.2)`, borderRadius:5,
                                color:C.red, fontSize:10, cursor:'pointer', flexShrink:0 }}>
                              <Trash2 size={10}/>
                            </button>
                          )}
                        </div>
                      ))
                    }
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            ADMIN TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'admin' && isAdmin && (
          <div>
            <h2 style={{ fontFamily:'Cinzel,serif', fontSize:18, color:C.text, marginBottom:20 }}>
              Edit Royal Palace Portal
            </h2>

            {/* Palace Info Panel */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:12, padding:24, marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between',
                alignItems:'center', marginBottom:16 }}>
                <h3 style={{ fontFamily:'Cinzel,serif', fontSize:15, color:C.gold, margin:0 }}>
                  Palace Information
                </h3>
                <button onClick={() => { setInfoForm({ ...palaceInfo }); setEditInfo(true); }}
                  style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 14px',
                    background:C.green, border:`1px solid rgba(201,168,76,0.3)`,
                    borderRadius:7, color:C.gold, fontSize:12, cursor:'pointer' }}>
                  <Edit2 size={12}/> Edit
                </button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[
                  { label:'Phone',        value:palaceInfo.phone },
                  { label:'Email',        value:palaceInfo.email },
                  { label:'Office Hours', value:palaceInfo.officeHours },
                  { label:'Address',      value:palaceInfo.address },
                ].map((f, i) => (
                  <div key={i} style={{ padding:'10px 14px', background:`${C.bg}80`,
                    borderRadius:8, border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:10, color:C.dim, textTransform:'uppercase',
                      letterSpacing:1, marginBottom:4 }}>{f.label}</div>
                    <div style={{ fontSize:13, color:C.text }}>{f.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {[
                { count:messages.length,  label:'Royal Messages',  tab:'messages' },
                { count:officials.length, label:'Palace Officials', tab:'structure' },
              ].map((s, i) => (
                <div key={i} style={{ background:C.card, border:`1px solid ${C.border}`,
                  borderRadius:12, padding:18, textAlign:'center' as const }}>
                  <div style={{ fontSize:24, fontWeight:700, color:C.gold }}>{s.count}</div>
                  <div style={{ fontSize:12, color:C.muted, marginBottom:10 }}>{s.label}</div>
                  <button onClick={() => setTab(s.tab as any)}
                    style={{ padding:'6px 14px', background:C.green,
                      border:`1px solid rgba(201,168,76,0.3)`, borderRadius:6,
                      color:C.gold, fontSize:11, cursor:'pointer' }}>
                    Manage →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL — Add / Edit Royal Message
      ══════════════════════════════════════════════════════════════════ */}
      {showMsgForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
          zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center',
          padding:20, overflowY:'auto' }}>
          <div style={{ background:'#0E1812', border:`1px solid ${C.border}`,
            borderRadius:16, padding:28, width:'100%', maxWidth:600, margin:'20px auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:20 }}>
              <h3 style={{ fontFamily:'Cinzel,serif', fontSize:16, color:C.gold, margin:0 }}>
                {editMsg ? 'Edit Royal Message' : '+ Add Royal Message'}
              </h3>
              <button onClick={() => setShowMsgForm(false)}
                style={{ background:'none', border:'none', color:C.muted,
                  fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { label:'Title *',             key:'title',   placeholder:'e.g. Royal Welcome Address' },
                { label:'Date',                key:'date',    placeholder:'e.g. December 24, 2026' },
                { label:'Occasion',            key:'occasion',placeholder:'e.g. Grand Opening Ceremony' },
                { label:'Excerpt (preview)',   key:'excerpt', placeholder:'Opening line…' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display:'block', fontSize:10, fontWeight:700,
                    letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                    {f.label}
                  </label>
                  <input value={(msgForm as any)[f.key]}
                    onChange={e => setMsgForm(p => ({ ...p, [f.key]:e.target.value }))}
                    placeholder={f.placeholder} style={inp}/>
                </div>
              ))}
              <div>
                <label style={{ display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                  Full Message Text *
                </label>
                <textarea value={msgForm.fullText} rows={6}
                  onChange={e => setMsgForm(p => ({ ...p, fullText:e.target.value }))}
                  placeholder="Full royal address text…"
                  style={{ ...inp, resize:'vertical' }}/>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <button onClick={() => setMsgForm(p => ({ ...p, isPublic:!p.isPublic }))}
                  style={{ width:44, height:24, borderRadius:12, border:'none',
                    background:msgForm.isPublic ? '#22c55e' : C.border,
                    cursor:'pointer', position:'relative', transition:'background 0.2s' }}>
                  <div style={{ position:'absolute', top:3,
                    left:msgForm.isPublic ? 22 : 3, width:18, height:18,
                    borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                </button>
                <span style={{ fontSize:13, color:C.muted }}>
                  {msgForm.isPublic ? '🌐 Published publicly' : '🔒 Private draft'}
                </span>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={handleSaveMsg} disabled={saving}
                style={{ flex:1, padding:'10px 20px', background:C.green,
                  border:`1px solid rgba(201,168,76,0.4)`, borderRadius:8,
                  color:C.gold, fontSize:13, fontWeight:700,
                  cursor:saving ? 'wait' : 'pointer', opacity:saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : editMsg ? '✅ Update Message' : '+ Add Message'}
              </button>
              <button onClick={() => setShowMsgForm(false)}
                style={{ padding:'10px 16px', background:'transparent',
                  border:`1px solid ${C.border}`, borderRadius:8,
                  color:C.muted, fontSize:13, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL — Add Palace Official
      ══════════════════════════════════════════════════════════════════ */}
      {showOfficialForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
          zIndex:1000, display:'flex', alignItems:'center',
          justifyContent:'center', padding:20 }}>
          <div style={{ background:'#0E1812', border:`1px solid ${C.border}`,
            borderRadius:16, padding:28, width:'100%', maxWidth:500 }}>
            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:20 }}>
              <h3 style={{ fontFamily:'Cinzel,serif', fontSize:16, color:C.gold, margin:0 }}>
                + Add Palace Official
              </h3>
              <button onClick={() => setShowOfficialForm(false)}
                style={{ background:'none', border:'none', color:C.muted,
                  fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {[
                { label:'Full Name *',       key:'name',       placeholder:'e.g. Chief John Idah' },
                { label:'Honorific',         key:'title',      placeholder:'e.g. Chief, Dr., Engr.' },
                { label:'Role / Position *', key:'role',       placeholder:'e.g. Palace Secretary' },
                { label:'Department',        key:'department', placeholder:'Palace Administration' },
                { label:'Phone',             key:'phone',      placeholder:'+234…' },
                { label:'Email',             key:'email',      placeholder:'official@domain.com' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display:'block', fontSize:10, fontWeight:700,
                    letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                    {f.label}
                  </label>
                  <input value={(officialForm as any)[f.key]}
                    onChange={e => setOfficialForm(p => ({ ...p, [f.key]:e.target.value }))}
                    placeholder={f.placeholder} style={inp}/>
                </div>
              ))}
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={{ display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                  Photo URL (Cloudinary)
                </label>
                <input value={officialForm.photo}
                  onChange={e => setOfficialForm(p => ({ ...p, photo:e.target.value }))}
                  placeholder="https://res.cloudinary.com/uuovmrek/…" style={inp}/>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={handleSaveOfficial} disabled={saving}
                style={{ flex:1, padding:'10px 20px', background:C.green,
                  border:`1px solid rgba(201,168,76,0.4)`, borderRadius:8,
                  color:C.gold, fontSize:13, fontWeight:700,
                  cursor:saving ? 'wait' : 'pointer', opacity:saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : '+ Add Official'}
              </button>
              <button onClick={() => setShowOfficialForm(false)}
                style={{ padding:'10px 16px', background:'transparent',
                  border:`1px solid ${C.border}`, borderRadius:8,
                  color:C.muted, fontSize:13, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL — Edit Palace Info
      ══════════════════════════════════════════════════════════════════ */}
      {editInfo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
          zIndex:1000, display:'flex', alignItems:'center',
          justifyContent:'center', padding:20 }}>
          <div style={{ background:'#0E1812', border:`1px solid ${C.border}`,
            borderRadius:16, padding:28, width:'100%', maxWidth:520 }}>
            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:20 }}>
              <h3 style={{ fontFamily:'Cinzel,serif', fontSize:16, color:C.gold, margin:0 }}>
                Edit Palace Information
              </h3>
              <button onClick={() => setEditInfo(false)}
                style={{ background:'none', border:'none', color:C.muted,
                  fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { label:'Phone',        key:'phone' },
                { label:'Email',        key:'email' },
                { label:'Office Hours', key:'officeHours' },
                { label:'Address',      key:'address' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display:'block', fontSize:10, fontWeight:700,
                    letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                    {f.label}
                  </label>
                  <input value={(infoForm as any)[f.key]}
                    onChange={e => setInfoForm(p => ({ ...p, [f.key]:e.target.value }))}
                    style={inp}/>
                </div>
              ))}
              <div>
                <label style={{ display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                  Patron Message (header)
                </label>
                <textarea value={infoForm.patronMessage} rows={3}
                  onChange={e => setInfoForm(p => ({ ...p, patronMessage:e.target.value }))}
                  style={{ ...inp, resize:'vertical' }}/>
              </div>
              <div>
                <label style={{ display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                  About Text
                </label>
                <textarea value={infoForm.aboutText} rows={4}
                  onChange={e => setInfoForm(p => ({ ...p, aboutText:e.target.value }))}
                  style={{ ...inp, resize:'vertical' }}/>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={handleSaveInfo} disabled={saving}
                style={{ flex:1, padding:'10px 20px', background:C.green,
                  border:`1px solid rgba(201,168,76,0.4)`, borderRadius:8,
                  color:C.gold, fontSize:13, fontWeight:700,
                  cursor:saving ? 'wait' : 'pointer', opacity:saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : '✅ Save Info'}
              </button>
              <button onClick={() => setEditInfo(false)}
                style={{ padding:'10px 16px', background:'transparent',
                  border:`1px solid ${C.border}`, borderRadius:8,
                  color:C.muted, fontSize:13, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.95); }
        }
      `}</style>
    </div>
  );
};

export default OgieneniPortal;
